import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { validateReportsSearch } from "@/modules/reports/presentation/lib/reports-search";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
const AuditPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditPage").then((module) => ({
    default: module.AuditPage,
  })),
);
const AuditDetailPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditDetailPage").then(
    (module) => ({
      default: module.AuditDetailPage,
    }),
  ),
);
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserCreatePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserCreatePage").then(
    (module) => ({
      default: module.UserCreatePage,
    }),
  ),
);
const UserEditPage = lazy(() =>
  import("@/modules/users/presentation/pages/UserEditPage").then((module) => ({
    default: module.UserEditPage,
  })),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const SettingsPage = lazy(() =>
  import("@/modules/settings/presentation/pages/SettingsPage").then(
    (module) => ({
      default: module.SettingsPage,
    }),
  ),
);
const ProductsPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductsPage").then(
    (module) => ({
      default: module.ProductsPage,
    }),
  ),
);
const ProductCreatePage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductCreatePage").then(
    (module) => ({
      default: module.ProductCreatePage,
    }),
  ),
);
const ProductEditPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductEditPage").then(
    (module) => ({
      default: module.ProductEditPage,
    }),
  ),
);
const ProductStockPage = lazy(() =>
  import("@/modules/products/presentation/pages/ProductStockPage").then(
    (module) => ({
      default: module.ProductStockPage,
    }),
  ),
);
const DuplicateConvertUnitPage = lazy(() =>
  import("@/modules/products/presentation/pages/DuplicateConvertUnitPage").then(
    (module) => ({
      default: module.DuplicateConvertUnitPage,
    }),
  ),
);
const OrdersListPage = lazy(() =>
  import("@/modules/orders/presentation/pages/OrdersListPage").then(
    (module) => ({
      default: module.OrdersListPage,
    }),
  ),
);
const QuickOrderPage = lazy(() =>
  import("@/modules/orders/presentation/pages/QuickOrderPage").then(
    (module) => ({
      default: module.QuickOrderPage,
    }),
  ),
);
const CustomJobsListPage = lazy(() =>
  import("@/modules/custom-jobs/presentation/pages/CustomJobsListPage").then(
    (module) => ({
      default: module.CustomJobsListPage,
    }),
  ),
);
const CustomJobFormPage = lazy(() =>
  import("@/modules/custom-jobs/presentation/pages/CustomJobFormPage").then(
    (module) => ({
      default: module.CustomJobFormPage,
    }),
  ),
);
const CustomJobDetailPage = lazy(() =>
  import("@/modules/custom-jobs/presentation/pages/CustomJobDetailPage").then(
    (module) => ({
      default: module.CustomJobDetailPage,
    }),
  ),
);
const CustomJobSummaryImagePage = lazy(() =>
  import(
    "@/modules/custom-jobs/presentation/pages/CustomJobSummaryImagePage"
  ).then((module) => ({
    default: module.CustomJobSummaryImagePage,
  })),
);
const RestockWaitlistPage = lazy(() =>
  import(
    "@/modules/restock-waitlist/presentation/pages/RestockWaitlistPage"
  ).then((module) => ({
    default: module.RestockWaitlistPage,
  })),
);
const RestockWaitlistFormPage = lazy(() =>
  import(
    "@/modules/restock-waitlist/presentation/pages/RestockWaitlistFormPage"
  ).then((module) => ({
    default: module.RestockWaitlistFormPage,
  })),
);
const RestockWaitlistDetailPage = lazy(() =>
  import(
    "@/modules/restock-waitlist/presentation/pages/RestockWaitlistDetailPage"
  ).then((module) => ({
    default: module.RestockWaitlistDetailPage,
  })),
);
const EventSalesPage = lazy(() =>
  import("@/modules/event-sales/presentation/pages/EventSalesPage").then(
    (module) => ({
      default: module.EventSalesPage,
    }),
  ),
);
const EventSaleDetailPage = lazy(() =>
  import("@/modules/event-sales/presentation/pages/EventSaleDetailPage").then(
    (module) => ({
      default: module.EventSaleDetailPage,
    }),
  ),
);
const CustomersPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomersPage").then(
    (module) => ({
      default: module.CustomersPage,
    }),
  ),
);
const CustomerDetailPage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomerDetailPage").then(
    (module) => ({
      default: module.CustomerDetailPage,
    }),
  ),
);
const CustomerMergePage = lazy(() =>
  import("@/modules/customers/presentation/pages/CustomerMergePage").then(
    (module) => ({
      default: module.CustomerMergePage,
    }),
  ),
);
const OrderDetailPage = lazy(() =>
  import("@/modules/orders/presentation/pages/OrderDetailPage").then(
    (module) => ({
      default: module.OrderDetailPage,
    }),
  ),
);
const FulfillmentBoardPage = lazy(() =>
  import("@/modules/fulfillment/presentation/pages/FulfillmentBoardPage").then(
    (module) => ({
      default: module.FulfillmentBoardPage,
    }),
  ),
);
const PackModePage = lazy(() =>
  import("@/modules/fulfillment/presentation/pages/PackModePage").then(
    (module) => ({
      default: module.PackModePage,
    }),
  ),
);
const ReportsPage = lazy(() =>
  import("@/modules/reports/presentation/pages/ReportsPage").then((module) => ({
    default: module.ReportsPage,
  })),
);
const ExpensesPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpensesPage").then(
    (module) => ({
      default: module.ExpensesPage,
    }),
  ),
);
const ExpenseCreatePage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpenseCreatePage").then(
    (module) => ({
      default: module.ExpenseCreatePage,
    }),
  ),
);
const ExpenseEditPage = lazy(() =>
  import("@/modules/expenses/presentation/pages/ExpenseEditPage").then(
    (module) => ({
      default: module.ExpenseEditPage,
    }),
  ),
);
const EditDraftOrderPage = lazy(() =>
  import("@/modules/orders/presentation/pages/EditDraftOrderPage").then(
    (module) => ({
      default: module.EditDraftOrderPage,
    }),
  ),
);
const ChatSnippetsPage = lazy(() =>
  import("@/modules/chat-tools/presentation/pages/ChatSnippetsPage").then(
    (module) => ({
      default: module.ChatSnippetsPage,
    }),
  ),
);
const InventoryReceiptsPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/InventoryReceiptsPage").then(
    (module) => ({
      default: module.InventoryReceiptsPage,
    }),
  ),
);
const ReceiveGoodsPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/ReceiveGoodsPage").then(
    (module) => ({
      default: module.ReceiveGoodsPage,
    }),
  ),
);
const GoodsReceiptDetailPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/GoodsReceiptDetailPage").then(
    (module) => ({
      default: module.GoodsReceiptDetailPage,
    }),
  ),
);
const GoodsReceiptEditPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/GoodsReceiptEditPage").then(
    (module) => ({
      default: module.GoodsReceiptEditPage,
    }),
  ),
);
const PlanningPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/PlanningPage").then(
    (module) => ({
      default: module.PlanningPage,
    }),
  ),
);
const BundleAssemblyPage = lazy(() =>
  import("@/modules/inventory/presentation/pages/BundleAssemblyPage").then(
    (module) => ({
      default: module.BundleAssemblyPage,
    }),
  ),
);
const ImportOrdersListPageP13 = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/import-orders/ImportOrdersListPage"
  ).then((module) => ({
    default: module.ImportOrdersListPage,
  })),
);
const ImportOrderNewPageP13 = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/import-orders/ImportOrderNewPage"
  ).then((module) => ({
    default: module.ImportOrderNewPage,
  })),
);
const ImportOrderDetailPageP13 = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/import-orders/ImportOrderDetailPage"
  ).then((module) => ({
    default: module.ImportOrderDetailPage,
  })),
);
const ImportOrderEditPageP13 = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/import-orders/ImportOrderEditPage"
  ).then((module) => ({
    default: module.ImportOrderEditPage,
  })),
);
const ImportInTransitReportPage = lazy(() =>
  import(
    "@/modules/inventory/presentation/pages/import-orders/ImportInTransitReportPage"
  ).then((module) => ({
    default: module.ImportInTransitReportPage,
  })),
);
const ShippingLabelPage = lazy(() =>
  import("@/modules/chat-tools/presentation/pages/ShippingLabelPage").then(
    (module) => ({
      default: module.ShippingLabelPage,
    }),
  ),
);
const OrderSummaryImagePage = lazy(() =>
  import("@/modules/chat-tools/presentation/pages/OrderSummaryImagePage").then(
    (module) => ({
      default: module.OrderSummaryImagePage,
    }),
  ),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/dashboard", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:ban"]}>
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit/$id",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:create"]}>
      <LazyPage>
        <UserCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:update"]}>
      <LazyPage>
        <UserEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: () => (
    <LazyPage>
      <SettingsPage />
    </LazyPage>
  ),
});

const productsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products",
  validateSearch: (search: Record<string, unknown>) => ({
    lowStock:
      search.lowStock === true ||
      search.lowStock === "true" ||
      search.lowStock === "1",
  }),
  component: () => (
    <RequirePermissions all={["products:read"]}>
      <LazyPage>
        <ProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/new",
  component: () => (
    <RequirePermissions all={["products:read"]} any={["products:write"]}>
      <LazyPage>
        <ProductCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

/** Legacy alias — some links used /products/create */
const productCreateAliasRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/create",
  beforeLoad: () => {
    throw redirect({ to: "/app/products/new", replace: true });
  },
});

const productEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/$id/edit",
  component: () => (
    <RequirePermissions all={["products:read"]} any={["products:write"]}>
      <LazyPage>
        <ProductEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const productStockRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/$id/stock",
  validateSearch: (search: Record<string, unknown>) => ({
    variantId:
      typeof search.variantId === "string" ? search.variantId : undefined,
    assign: search.assign === true || search.assign === "1",
  }),
  component: () => (
    <RequirePermissions all={["products:read"]}>
      <LazyPage>
        <ProductStockPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const duplicateConvertUnitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/products/$id/duplicate-convert",
  validateSearch: (search: Record<string, unknown>) => ({
    variantId:
      typeof search.variantId === "string" ? search.variantId : undefined,
  }),
  component: () => (
    <RequirePermissions all={["products:read"]} any={["products:write"]}>
      <LazyPage>
        <DuplicateConvertUnitPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const ordersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrdersListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const orderCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/new",
  validateSearch: (search: Record<string, unknown>) => ({
    customerId:
      typeof search.customerId === "string" ? search.customerId : undefined,
    waitlistId:
      typeof search.waitlistId === "string" ? search.waitlistId : undefined,
  }),
  component: () => (
    <RequirePermissions all={["orders:read"]} any={["orders:write"]}>
      <LazyPage>
        <QuickOrderPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <CustomersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customerMergeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/merge",
  component: () => (
    <RequirePermissions all={["orders:write"]}>
      <LazyPage>
        <CustomerMergePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/customers/$id",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <CustomerDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const orderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$id",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrderDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const orderEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$id/edit",
  component: () => (
    <RequirePermissions all={["orders:read"]} any={["orders:write"]}>
      <LazyPage>
        <EditDraftOrderPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customJobsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/custom-jobs",
  component: () => (
    <RequirePermissions all={["custom_jobs:read"]}>
      <LazyPage>
        <CustomJobsListPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customJobCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/custom-jobs/new",
  component: () => (
    <RequirePermissions all={["custom_jobs:read"]} any={["custom_jobs:write"]}>
      <LazyPage>
        <CustomJobFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customJobDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/custom-jobs/$id",
  component: () => (
    <RequirePermissions all={["custom_jobs:read"]}>
      <LazyPage>
        <CustomJobDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customJobEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/custom-jobs/$id/edit",
  component: () => (
    <RequirePermissions all={["custom_jobs:read"]} any={["custom_jobs:write"]}>
      <LazyPage>
        <CustomJobFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const customJobSummaryImageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/custom-jobs/$id/summary-image",
  component: () => (
    <RequirePermissions all={["custom_jobs:read"]}>
      <LazyPage>
        <CustomJobSummaryImagePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const restockWaitlistRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/restock-waitlist",
  validateSearch: (search: Record<string, unknown>) => ({
    status: typeof search.status === "string" ? search.status : undefined,
  }),
  component: () => (
    <RequirePermissions all={["restock_waitlist:read"]}>
      <LazyPage>
        <RestockWaitlistPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const restockWaitlistCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/restock-waitlist/new",
  component: () => (
    <RequirePermissions
      all={["restock_waitlist:read"]}
      any={["restock_waitlist:write"]}
    >
      <LazyPage>
        <RestockWaitlistFormPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const restockWaitlistDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/restock-waitlist/$id",
  component: () => (
    <RequirePermissions all={["restock_waitlist:read"]}>
      <LazyPage>
        <RestockWaitlistDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventSalesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/event-sales",
  component: () => (
    <RequirePermissions all={["event_sales:read"]}>
      <LazyPage>
        <EventSalesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const eventSaleDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/event-sales/$id",
  component: () => (
    <RequirePermissions all={["event_sales:read"]}>
      <LazyPage>
        <EventSaleDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const fulfillmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <FulfillmentBoardPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const fulfillmentPackRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fulfillment/pack",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <PackModePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  validateSearch: validateReportsSearch,
  component: () => (
    <RequirePermissions any={["reports:read", "orders:read"]}>
      <LazyPage>
        <ReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reportsProfitRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports/profit",
  beforeLoad: () => {
    throw redirect({
      to: "/app/reports",
      search: { tab: "profit", section: "overview" },
    });
  },
});

const expensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses",
  component: () => (
    <RequirePermissions any={["expenses:read", "reports:read", "orders:read"]}>
      <LazyPage>
        <ExpensesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expenseCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/new",
  component: () => (
    <RequirePermissions all={["expenses:read"]} any={["expenses:write"]}>
      <LazyPage>
        <ExpenseCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const expenseEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses/$id/edit",
  component: () => (
    <RequirePermissions all={["expenses:read"]} any={["expenses:write"]}>
      <LazyPage>
        <ExpenseEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const chatSnippetsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/chat-snippets",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <ChatSnippetsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shippingLabelRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$id/shipping-label",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <ShippingLabelPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const orderSummaryImageRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orders/$id/summary-image",
  component: () => (
    <RequirePermissions all={["orders:read"]}>
      <LazyPage>
        <OrderSummaryImagePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <InventoryReceiptsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryPlanningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/planning",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <PlanningPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryAssemblyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/assembly",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:write"]}>
      <LazyPage>
        <BundleAssemblyPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryReceiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/receive",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:write"]}>
      <LazyPage>
        <ReceiveGoodsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryReceiptDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/receive/$id",
  component: () => (
    <RequirePermissions all={["inventory:read"]}>
      <LazyPage>
        <GoodsReceiptDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryReceiptEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/receive/$id/edit",
  component: () => (
    <RequirePermissions all={["inventory:read"]} any={["inventory:write"]}>
      <LazyPage>
        <GoodsReceiptEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const inventoryImportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/imports",
  beforeLoad: () => {
    throw redirect({ to: "/app/import-orders" });
  },
});

const inventoryImportNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/imports/new",
  beforeLoad: () => {
    throw redirect({ to: "/app/import-orders/new" });
  },
});

const inventoryImportDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/imports/$id",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/app/import-orders/$id",
      params: { id: params.id },
    });
  },
});

const importOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/import-orders",
  component: () => (
    <RequirePermissions all={["import_orders:read"]}>
      <LazyPage>
        <ImportOrdersListPageP13 />
      </LazyPage>
    </RequirePermissions>
  ),
});

const importOrderNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/import-orders/new",
  component: () => (
    <RequirePermissions
      all={["import_orders:read"]}
      any={["import_orders:write"]}
    >
      <LazyPage>
        <ImportOrderNewPageP13 />
      </LazyPage>
    </RequirePermissions>
  ),
});

const importInTransitReportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/import-orders/reports/in-transit",
  component: () => (
    <RequirePermissions all={["import_orders:read"]}>
      <LazyPage>
        <ImportInTransitReportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const importOrderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/import-orders/$id",
  component: () => (
    <RequirePermissions all={["import_orders:read"]}>
      <LazyPage>
        <ImportOrderDetailPageP13 />
      </LazyPage>
    </RequirePermissions>
  ),
});

const importOrderEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/import-orders/$id/edit",
  component: () => (
    <RequirePermissions
      all={["import_orders:read"]}
      any={["import_orders:write"]}
    >
      <LazyPage>
        <ImportOrderEditPageP13 />
      </LazyPage>
    </RequirePermissions>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    dashboardRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    usersRoute,
    userCreateRoute,
    userEditRoute,
    auditRoute,
    auditDetailRoute,
    profileRoute,
    settingsRoute,
    productsRoute,
    productCreateRoute,
    productCreateAliasRoute,
    productEditRoute,
    productStockRoute,
    duplicateConvertUnitRoute,
    ordersRoute,
    orderCreateRoute,
    orderEditRoute,
    orderDetailRoute,
    customJobsRoute,
    customJobCreateRoute,
    customJobDetailRoute,
    customJobEditRoute,
    customJobSummaryImageRoute,
    restockWaitlistRoute,
    restockWaitlistCreateRoute,
    restockWaitlistDetailRoute,
    eventSalesRoute,
    eventSaleDetailRoute,
    customersRoute,
    customerMergeRoute,
    customerDetailRoute,
    fulfillmentRoute,
    fulfillmentPackRoute,
    reportsRoute,
    reportsProfitRoute,
    expensesRoute,
    expenseCreateRoute,
    expenseEditRoute,
    chatSnippetsRoute,
    shippingLabelRoute,
    orderSummaryImageRoute,
    inventoryRoute,
    inventoryPlanningRoute,
    inventoryAssemblyRoute,
    inventoryReceiveRoute,
    inventoryReceiptDetailRoute,
    inventoryReceiptEditRoute,
    inventoryImportsRoute,
    inventoryImportNewRoute,
    inventoryImportDetailRoute,
    importOrdersRoute,
    importOrderNewRoute,
    importInTransitReportRoute,
    importOrderDetailRoute,
    importOrderEditRoute,
  ]),
  forbiddenRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
